import type { AllowedPartners } from "@expert/expert-university";

import { useExpertUniversityStore } from "@expert/expert-university";
import { showNotification } from "@mantine/notifications";
import { type ReactElement, useCallback, useEffect, useRef, useState } from "react";

import TrainingCallStartInterstitial from "../components/ascend/TrainingCallStartInterstitial";

type CallStartState = {
    agentReady: boolean;
    hasError: boolean;
    /** True after Start until `training-session-started` — avoids a tile-grid flash during join. */
    isJoiningCall: boolean;
    profileId: string;
    partner: AllowedPartners;
    isAscendProfile: boolean;
    isLiveKitSession: boolean;
};

type BeginCallStartArgs = {
    profileId: string;
    partner: AllowedPartners;
    isAscendProfile?: boolean;
    isLiveKitSession?: boolean;
};

/** Shared pre-call interstitial: wait until ready, then Start accepts the deferred task. */
export function useLiveKitCallStartInterstitial(): {
    beginCallStart: (args: BeginCallStartArgs) => void;
    callStartScreen: ReactElement | null;
} {
    const eventBus = useExpertUniversityStore((state) => state.eventBus);
    const [callStartInterstitial, setCallStartInterstitial] = useState<CallStartState | undefined>(undefined);
    const callStartInterstitialRef = useRef(callStartInterstitial);
    callStartInterstitialRef.current = callStartInterstitial;

    useEffect(() => {
        if (!eventBus) {
            return;
        }

        const offFailed = eventBus.on("failed-to-start-training-session", () => {
            if (callStartInterstitialRef.current) {
                setCallStartInterstitial({
                    ...callStartInterstitialRef.current,
                    agentReady: false,
                    hasError: true,
                    isJoiningCall: false,
                });
                return;
            }
            showNotification({
                id: "start-training-session-error",
                position: "top-center",
                withCloseButton: true,
                autoClose: 3000,
                title: "Unable to start training Session",
                message: "There was a problem starting the training session. Please try again.",
                color: "red",
                loading: false,
            });
        });
        const offAgentReady = eventBus.on("livekit-agent-ready", () => {
            setCallStartInterstitial((current) =>
                current && !current.hasError && !current.isJoiningCall ? { ...current, agentReady: true } : current,
            );
        });
        const offTrainingSessionStarted = eventBus.on("training-session-started", () => {
            setCallStartInterstitial(undefined);
        });
        return () => {
            offFailed();
            offAgentReady();
            offTrainingSessionStarted();
        };
    }, [eventBus]);

    const beginCallStart = useCallback(
        ({ profileId, partner, isAscendProfile = false, isLiveKitSession = false }: BeginCallStartArgs) => {
            setCallStartInterstitial({
                agentReady: false,
                hasError: false,
                isJoiningCall: false,
                profileId,
                partner,
                isAscendProfile,
                isLiveKitSession,
            });
        },
        [],
    );

    const callStartScreen = callStartInterstitial ? (
        <TrainingCallStartInterstitial
            agentReady={callStartInterstitial.agentReady}
            hasError={callStartInterstitial.hasError}
            isJoiningCall={callStartInterstitial.isJoiningCall}
            onStartCall={() => {
                setCallStartInterstitial((current) =>
                    current ? { ...current, isJoiningCall: true, hasError: false } : current,
                );
                eventBus?.emit("join-livekit-call", {});
            }}
            onRetry={() => {
                if (!eventBus) {
                    return;
                }
                setCallStartInterstitial({
                    ...callStartInterstitial,
                    agentReady: false,
                    hasError: false,
                    isJoiningCall: false,
                });
                eventBus.emit("start-simulation-training", {
                    profileId: callStartInterstitial.profileId,
                    partner: callStartInterstitial.partner,
                    liveKitConfiguration: {
                        isLiveKitSession: callStartInterstitial.isLiveKitSession,
                        isAscendProfile: callStartInterstitial.isAscendProfile,
                    },
                });
            }}
            onCancel={() => {
                eventBus?.emit("cancel-livekit-call-start", {});
                setCallStartInterstitial(undefined);
            }}
        />
    ) : null;

    return { beginCallStart, callStartScreen };
}
